(function () {
  if (window.customElements.get('pi-bundle-and-save')) return;

  class PiBundleAndSave extends HTMLElement {
    connectedCallback() {
      if (this._initialized) return;
      this._initialized = true;

      const configNode = this.querySelector('[data-pi-bms-config]');
      this.config = configNode ? JSON.parse(configNode.textContent) : {};
      this.sectionId = this.dataset.sectionId;
      this.formId = this.dataset.formId;
      this.tiers = this.config.tiers || [];
      this.variants = this.config.variants || [];
      this.moneyFormat = this.config.moneyFormat;
      this.priceEachLabel = this.config.priceEachLabel || '/each';
      this.basePrice = this.config.variant?.price || 0;
      this.selectedVariantId = this.config.variant?.id;
      this.selectedTierIndex = this.config.defaultTierIndex ?? 0;

      this.tierInputs = this.querySelectorAll('.pi-bms__tier-input');
      this.tierElements = this.querySelectorAll('.pi-bms__tier');

      this.onTierChange = this.onTierChange.bind(this);
      this.onVariantSelectChange = this.onVariantSelectChange.bind(this);
      this.onVariantChange = this.onVariantChange.bind(this);

      this.tierInputs.forEach((input) => input.addEventListener('change', this.onTierChange));
      this.querySelectorAll('[data-pi-bms-variant-select]').forEach((select) => {
        select.addEventListener('change', this.onVariantSelectChange);
      });

      if (typeof subscribe === 'function') {
        this.variantChangeUnsub = subscribe(PUB_SUB_EVENTS.variantChange, this.onVariantChange);
      }

      this.selectTier(this.selectedTierIndex, true);
      this.updateAllPrices();
    }

    disconnectedCallback() {
      this.variantChangeUnsub?.();
      this._initialized = false;
    }

    onVariantChange({ data }) {
      if (!data || String(data.sectionId) !== String(this.sectionId)) return;
      if (!data.variant) return;

      this.basePrice = data.variant.price;
      this.selectedVariantId = data.variant.id;
      this.syncVariantSelects(data.variant.id);
      this.updateAllPrices();

      const tier = this.tiers[this.selectedTierIndex];
      if (tier) {
        this.updateQuantity(tier.quantity);
      }
    }

    onTierChange(event) {
      const index = parseInt(event.target.value, 10);
      if (Number.isNaN(index)) return;
      this.selectTier(index, true);
    }

    selectTier(index, updateQuantity) {
      this.selectedTierIndex = index;

      this.tierElements.forEach((element, tierIndex) => {
        element.classList.toggle('pi-bms__tier--selected', tierIndex === index);
      });

      const tier = this.tiers[index];
      if (updateQuantity && tier) {
        this.updateQuantity(tier.quantity);
      }
    }

    updateQuantity(quantity) {
      const productInfo = this.closest('product-info');
      const quantityInput = productInfo?.querySelector('.quantity__input');
      if (!quantityInput || !quantity) return;

      quantityInput.value = quantity;
      quantityInput.dispatchEvent(new Event('change', { bubbles: true }));

      if (typeof publish === 'function') {
        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }
    }

    onVariantSelectChange(event) {
      const variantId = parseInt(event.target.value, 10);
      if (Number.isNaN(variantId)) return;
      this.setVariant(variantId);
    }

    setVariant(variantId) {
      const variant = this.variants.find((entry) => entry.id === variantId);
      if (!variant) return;

      this.basePrice = variant.price;
      this.selectedVariantId = variantId;
      this.syncMainVariantPicker(variant);
      this.updateAllPrices();
    }

    syncMainVariantPicker(variant) {
      const variantSelects = this.closest('product-info')?.querySelector('variant-selects');
      if (!variantSelects) {
        this.updateVariantInputs(variant.id);
        return;
      }

      let changed = false;

      variantSelects.querySelectorAll('select').forEach((select, index) => {
        const optionValue = variant[`option${index + 1}`];
        if (!optionValue || select.value === optionValue) return;
        select.value = optionValue;
        changed = true;
        select.dispatchEvent(new Event('change', { bubbles: true }));
      });

      variantSelects.querySelectorAll('fieldset').forEach((fieldset, index) => {
        const optionValue = variant[`option${index + 1}`];
        if (!optionValue) return;
        const radio = fieldset.querySelector(`input[type='radio'][value="${this.escapeValue(optionValue)}"]`);
        if (!radio || radio.checked) return;
        radio.checked = true;
        changed = true;
        radio.dispatchEvent(new Event('change', { bubbles: true }));
      });

      if (!changed) {
        this.updateVariantInputs(variant.id);
      }
    }

    updateVariantInputs(variantId) {
      const productInfo = this.closest('product-info');
      if (!productInfo) return;

      productInfo.querySelectorAll(`#${this.formId} input[name='id']`).forEach((input) => {
        input.value = variantId;
        input.disabled = false;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }

    syncVariantSelects(variantId) {
      this.querySelectorAll('[data-pi-bms-variant-select]').forEach((select) => {
        if (select.value !== String(variantId)) {
          select.value = String(variantId);
        }
      });
    }

    calculateDiscountedPrice(discountPercent) {
      const discount = Number(discountPercent) || 0;
      return Math.round((this.basePrice * (100 - discount)) / 100);
    }

    formatPrice(cents) {
      if (window.PiUtils?.formatMoney) {
        return window.PiUtils.formatMoney(cents, this.moneyFormat);
      }
      if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
        return Shopify.formatMoney(cents, this.moneyFormat);
      }
      return (cents / 100).toFixed(2);
    }

    updateAllPrices() {
      this.tierElements.forEach((element, index) => {
        const tier = this.tiers[index];
        if (!tier) return;

        const discountedPrice = this.calculateDiscountedPrice(tier.discountValue);
        const priceElement = element.querySelector('[data-pi-bms-price]');
        const compareElement = element.querySelector('[data-pi-bms-compare]');

        if (priceElement) {
          priceElement.textContent = `${this.formatPrice(discountedPrice)} ${this.priceEachLabel}`;
        }

        if (compareElement) {
          if (Number(tier.discountValue) > 0) {
            compareElement.textContent = this.formatPrice(this.basePrice);
            compareElement.hidden = false;
          } else {
            compareElement.textContent = '';
            compareElement.hidden = true;
          }
        }
      });
    }

    escapeValue(value) {
      if (typeof CSS !== 'undefined' && typeof CSS.escape === 'function') {
        return CSS.escape(value);
      }
      return String(value).replace(/\\/g, '\\\\').replace(/"/g, '\\"');
    }
  }

  window.customElements.define('pi-bundle-and-save', PiBundleAndSave);
})();
